import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { fmtDate, COMPLIANCE_COLORS, PRIORITY_STYLES } from "@/lib/format";
import { CalendarDays, ClipboardCheck } from "lucide-react";

export default function CalendarPage() {
    const [events, setEvents] = useState([]);
    const [month, setMonth] = useState(new Date());
    const [selected, setSelected] = useState(new Date());

    useEffect(() => {
        const start = new Date(month.getFullYear(), month.getMonth(), 1).toISOString().slice(0, 10);
        const end = new Date(month.getFullYear(), month.getMonth() + 1, 0).toISOString().slice(0, 10);
        api.get("/calendar", { params: { start, end } }).then((r) => setEvents(r.data));
    }, [month]);

    const byDate = useMemo(() => {
        const m = new Map();
        events.forEach((e) => {
            const k = (e.date || "").slice(0, 10);
            if (!k) return;
            m.set(k, [...(m.get(k) || []), e]);
        });
        return m;
    }, [events]);

    const key = selected?.toISOString().slice(0, 10);
    const dayEvents = byDate.get(key) || [];

    return (
        <div className="space-y-6" data-testid="calendar-page">
            <PageHeader title="Calendar" subtitle="Compliance deadlines and task due-dates at a glance" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-4 lg:col-span-2">
                    <Calendar
                        mode="single"
                        selected={selected}
                        onSelect={(d) => d && setSelected(d)}
                        month={month}
                        onMonthChange={setMonth}
                        showOutsideDays
                        modifiers={{ hasEvent: (d) => byDate.has(d.toISOString().slice(0, 10)) }}
                        modifiersClassNames={{
                            hasEvent:
                                "relative after:content-[''] after:absolute after:left-1/2 after:bottom-1 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-primary",
                        }}
                        className="rounded-md"
                    />
                </Card>
                <Card className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <CalendarDays size={16} className="text-primary" />
                        <div className="font-display font-semibold text-slate-800">
                            {fmtDate(selected)}
                        </div>
                    </div>
                    {dayEvents.length === 0 ? (
                        <div className="text-sm text-slate-500 py-6 text-center border rounded-md">
                            Nothing scheduled.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {dayEvents.map((e) => (
                                <li
                                    key={e.id}
                                    className="p-3 rounded-md border border-slate-200 bg-white"
                                    data-testid={`event-${e.id}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
                                            {e.kind === "task" ? <ClipboardCheck size={12} /> : <CalendarDays size={12} />}
                                            {e.kind}
                                        </div>
                                        {e.priority && (
                                            <span className={`badge-status ${PRIORITY_STYLES[e.priority]}`}>
                                                {e.priority}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm font-medium text-slate-800 mt-1">{e.title}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
}
